import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import ClientCombobox from '@/components/ClientCombobox';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  ClipboardList, Plus, Clock, Hash, Star, Edit3, Trash2,
  Calendar, TrendingUp, Flame, CheckCircle2, Target, FileSpreadsheet,
  Download, Users, GitMerge, AlertTriangle, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import CollaboratorPicker from '@/components/CollaboratorPicker';
import CollaboratorChips from '@/components/CollaboratorChips';

const STATUS_COLOR = {
  Ongoing:   'bg-green-tint text-green-ink border-transparent',
  Submitted: 'bg-brand-tint text-brand-ink border-transparent',
  Accepted:  'bg-green-tint text-green-ink border-transparent',
  Completed: 'bg-ink text-white border-transparent',
  Rework:    'bg-orange-tint text-rust border-transparent',
};

function StatCard({ icon: Icon, label, value, sub, color = 'amber' }) {
  return (
    <Card className="shine-card p-4 rounded-xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{value}</div>
          {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
        <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-amber-500/10 border border-amber-500/30">
          <Icon className="h-5 w-5 text-amber-300" />
        </div>
      </div>
    </Card>
  );
}

function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color = pct >= 75 ? '#39b563' : pct >= 50 ? '#5780ea' : pct >= 30 ? '#fbbf24' : '#f97373';
  return (
    <div className="relative h-20 w-20">
      <svg viewBox="0 0 70 70" className="h-20 w-20 -rotate-90">
        <circle cx="35" cy="35" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle cx="35" cy="35" r={r} stroke={color} strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-extrabold text-white leading-none">{Math.round(pct)}</span>
        <span className="text-[9px] uppercase text-slate-400 tracking-wider">score</span>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { user, isManager, isOwner } = useAuth();
  const isClient = user?.role === 'Client';
  // Only Owner / Admin (true admins) can edit or delete log reports.
  const canEditEntry = isOwner;

  const exportReports = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.user_id) params.set('user_id', filters.user_id);
      if (filters.client_id) params.set('client_id', filters.client_id);
      if (filters.format) params.set('format_name', filters.format);
      if (filters.status) params.set('status', filters.status);
      const token = localStorage.getItem('shine_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/export?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const t = await res.text();
        toast.error('Export failed: ' + t);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const range = `${filters.date_from || 'start'}_to_${filters.date_to || 'today'}`;
      a.download = `shine_daily_reports_${range}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export error: ' + err.message);
    }
  };
  const [meta, setMeta] = useState({ formats: [], statuses: [] });
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportUsers, setReportUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('week');
  const [mine, setMine] = useState(!isManager);
  const [filters, setFilters] = useState({ user_id: '', client_id: '', format: '', status: '', date_from: '', date_to: '' });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const canBackdate = isManager; // Owner/Operations can pick any date
  const [form, setForm] = useState({
    date: today, client_id: '', company_name: '', project_id: '',
    format: 'Creative Design', custom_format: '', item_description: '', count: 1, hours: 1.0,
    status: 'Ongoing', notes: '', collaborators: [],
  });

  const loadMeta = async () => {
    const [m, c, p, u, ru] = await Promise.all([
      api.get('/daily-reports/meta'),
      api.get('/clients').catch(() => ({ data: [] })),
      api.get('/projects/picker').catch(() => ({ data: [] })),
      // Load team list for both managers (filter dropdown) and non-managers
      // (collaborator picker). Clients can't set collaborators, so skip for them.
      !isClient ? api.get('/users').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      // Scope-filtered users for the Daily Reports member filter + Employee
      // Report picker — honours the viewer's daily_reports_scope.
      !isClient ? api.get('/users?for_reports=true').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setMeta(m.data);
    setClients(c.data);
    setProjects(p.data);
    setUsers(u.data);
    setReportUsers(ru.data);
  };

  const load = async () => {
    const params = new URLSearchParams();
    if (mine) params.set('mine', 'true');
    if (filters.user_id) params.set('user_id', filters.user_id);
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.format) params.set('format', filters.format);
    if (filters.status) params.set('status', filters.status);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    const res = await api.get('/daily-reports' + (params.toString() ? `?${params}` : ''));
    setRows(res.data);
  };

  const loadStats = async () => {
    try {
      const res = await api.get(`/daily-reports/stats/my?period=${period}`);
      setStats(res.data);
    } catch (_) { /* stats optional */ }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); }, [mine, filters]);
  useEffect(() => { if (!isClient) loadStats(); }, [period, isClient]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      date: today, client_id: '', company_name: '', project_id: '',
      format: meta.formats[0] || 'Creative Design',
      custom_format: '',
      item_description: '', count: 1, hours: 1.0, status: 'Ongoing', notes: '',
      collaborators: [],
    });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date, client_id: r.client_id || '', company_name: r.company_name,
      project_id: r.project_id || '', format: r.format,
      custom_format: r.custom_format || '',
      item_description: r.item_description, count: r.count, hours: r.hours,
      status: r.status, notes: r.notes || '',
      collaborators: Array.isArray(r.collaborators) ? r.collaborators : [],
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      count: parseInt(form.count) || 1,
      hours: parseFloat(form.hours) || 0,
    };
    if (!payload.client_id) delete payload.client_id;
    if (!payload.project_id) delete payload.project_id;
    if (!payload.company_name && payload.client_id) {
      const c = clients.find((x) => x.id === payload.client_id);
      if (c) payload.company_name = c.company;
    }
    if (!payload.company_name) {
      alert('Please pick a company or type one in.');
      return;
    }
    if (payload.format === 'Other' && !payload.custom_format?.trim()) {
      alert("Please specify what work you did for 'Other' format. It will go to Admin for approval.");
      return;
    }
    if (payload.format !== 'Other') delete payload.custom_format;
    try {
      if (editing) await api.patch(`/daily-reports/${editing.id}`, payload);
      else {
        const res = await api.post('/daily-reports', payload);
        // Soft duplicate nudge — backend never blocks, just returns IDs.
        const dupes = res.data?.possible_duplicate_of || [];
        if (dupes.length) {
          const first = dupes[0];
          alert(
            `Heads-up: you have a similar Ongoing entry (${first.date} — ${first.item_description || 'no description'}). ` +
            `Consider using "Log hours" on it next time instead of creating a new one.`
          );
        }
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save. Please try again.');
      return;
    }
    setOpen(false);
    setEditing(null);
    load();
    loadStats();
  };

  // ------ "Continue Ongoing?" prompt + Log-Hours flow ------
  //
  // Feature flag — the "Continue an Ongoing item?" picker is paused per Part B
  // of the Oct 2026 Reports Fix prompt. Work Items now have an explicit
  // Ongoing/Submitted/Completed lifecycle with required hours, so re-prompting
  // on every + Log work click is redundant.
  //
  // Flip this to `true` to bring the popup back without a rebuild. The whole
  // picker UI below (lines ~940-1000) is retained; only the trigger is gated.
  const DUPLICATE_CONTINUE_PROMPT_ENABLED = false;

  const [ongoingList, setOngoingList] = useState([]);
  const [ongoingOpen, setOngoingOpen] = useState(false);
  const [logHoursOn, setLogHoursOn] = useState(null); // report being logged against
  const [logHoursForm, setLogHoursForm] = useState({ date: '', hours: 1.0, note: '' });

  const openLogWorkFlow = async () => {
    if (!DUPLICATE_CONTINUE_PROMPT_ENABLED) {
      // Straight to create, no re-prompt.
      openCreate();
      return;
    }
    // Legacy path (kept behind the flag): fetch this user's currently-Ongoing
    // entries first — if any, show the "Continue or start new?" picker.
    try {
      const res = await api.get('/daily-reports/mine/ongoing');
      const rows = res.data || [];
      setOngoingList(rows);
      if (rows.length > 0) {
        setOngoingOpen(true);
        return;
      }
    } catch (_) { /* fall through to normal create */ }
    openCreate();
  };

  const continueEntry = (rep) => {
    setOngoingOpen(false);
    setLogHoursOn(rep);
    setLogHoursForm({ date: new Date().toISOString().slice(0, 10), hours: 1.0, note: '' });
  };

  const submitLogHours = async () => {
    if (!logHoursOn) return;
    if (!logHoursForm.date || !(parseFloat(logHoursForm.hours) > 0)) {
      alert('Please enter a valid date and hours (>0).');
      return;
    }
    try {
      await api.post(`/daily-reports/${logHoursOn.id}/log-hours`, {
        date: logHoursForm.date,
        hours: parseFloat(logHoursForm.hours),
        note: logHoursForm.note || null,
      });
      setLogHoursOn(null);
      load();
      loadStats();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not log hours.');
    }
  };

  const reopenReport = async (r) => {
    const note = prompt('Reason for reopening (required):');
    if (!note || !note.trim()) return;
    try {
      await api.post(`/daily-reports/${r.id}/reopen`, { note: note.trim() });
      load();
      loadStats();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not reopen entry.');
    }
  };

  // --- Status change dialog (Mark Submitted / Mark Completed) ---
  // Hours are REQUIRED here. For creators we enforce > 0 client-side; privileged
  // users may send 0 (backend also enforces the rule).
  const [statusTarget, setStatusTarget] = useState(null);   // { report, newStatus }
  const [statusForm, setStatusForm] = useState({ hours: 1.0, note: '' });
  const openStatusDialog = (rep, newStatus) => {
    setStatusTarget({ report: rep, newStatus });
    setStatusForm({ hours: 1.0, note: '' });
  };
  const submitStatusChange = async () => {
    if (!statusTarget) return;
    const privileged = ['Owner', 'Admin', 'Operations'].includes(user?.role);
    const h = parseFloat(statusForm.hours);
    if (!privileged && !(h > 0)) {
      alert('Hours are required to change status. Enter the hours you spent on this.');
      return;
    }
    try {
      await api.post(`/daily-reports/${statusTarget.report.id}/status`, {
        status: statusTarget.newStatus,
        hours: isNaN(h) ? 0 : h,
        note: statusForm.note || null,
      });
      setStatusTarget(null);
      load();
      loadStats();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not change status.');
    }
  };

  // --- Download Client Report (client REQUIRED, counts + status only) ---
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadForm, setDownloadForm] = useState({
    client_id: '',
    project_id: '',
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    format: 'pdf',
  });
  const projectsForDownload = useMemo(() => {
    if (!downloadForm.client_id) return [];
    return projects.filter((p) => p.client_id === downloadForm.client_id);
  }, [downloadForm.client_id, projects]);

  const applyDatePreset = (which) => {
    const today = new Date();
    const toStr = (d) => d.toISOString().slice(0, 10);
    if (which === 'this_month') {
      setDownloadForm((f) => ({
        ...f,
        date_from: toStr(new Date(today.getFullYear(), today.getMonth(), 1)),
        date_to: toStr(today),
      }));
    } else if (which === 'last_month') {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      setDownloadForm((f) => ({ ...f, date_from: toStr(start), date_to: toStr(end) }));
    }
  };

  const downloadClientReport = async () => {
    if (!downloadForm.client_id) {
      toast.error('Please pick a client — the Client Report cannot be run across all clients.');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.set('client_id', downloadForm.client_id);
      if (downloadForm.project_id) params.set('project_id', downloadForm.project_id);
      if (downloadForm.date_from) params.set('date_from', downloadForm.date_from);
      if (downloadForm.date_to) params.set('date_to', downloadForm.date_to);
      params.set('format', downloadForm.format);
      const resp = await api.get(`/reports/client-work?${params}`, { responseType: 'blob' });
      // Empty-state detection — backend returns a JSON `{empty:true, message}`
      // instead of a PDF/XLSX blob when there's nothing to report.
      const contentType = resp.data?.type || resp.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await resp.data.text();
        try {
          const info = JSON.parse(text);
          if (info?.empty) {
            toast.info(info.message || 'No work found in this range.');
            return;
          }
        } catch (_) { /* fall through */ }
      }
      const ext = downloadForm.format === 'xlsx' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      const client = clients.find((c) => c.id === downloadForm.client_id);
      const label = (client?.company || 'client').replace(/[^A-Za-z0-9_\-]/g, '_');
      a.download = `shine_client_report_${label}_${downloadForm.date_from || 'start'}_to_${downloadForm.date_to || new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setDownloadOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  // --- Download Employee Report (internal, hours + names) ---
  const [empDownloadOpen, setEmpDownloadOpen] = useState(false);
  const [empForm, setEmpForm] = useState({
    user_id: 'all',
    client_id: '',
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    format: 'pdf',
  });
  const downloadEmployeeReport = async () => {
    try {
      const params = new URLSearchParams();
      params.set('user_id', empForm.user_id || 'all');
      if (empForm.client_id) params.set('client_id', empForm.client_id);
      if (empForm.date_from) params.set('date_from', empForm.date_from);
      if (empForm.date_to) params.set('date_to', empForm.date_to);
      params.set('format', empForm.format);
      const resp = await api.get(`/reports/employee-work?${params}`, { responseType: 'blob' });
      const contentType = resp.data?.type || resp.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await resp.data.text();
        try {
          const info = JSON.parse(text);
          if (info?.empty) {
            toast.info(info.message || 'No work found in this range.');
            return;
          }
        } catch (_) { /* fall through */ }
      }
      const ext = empForm.format === 'xlsx' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      const label = empForm.user_id === 'all'
        ? 'team'
        : ((users.find((u) => u.id === empForm.user_id)?.name) || 'employee').replace(/[^A-Za-z0-9_\-]/g, '_');
      a.download = `shine_employee_report_${label}_${empForm.date_from || 'start'}_to_${empForm.date_to || new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setEmpDownloadOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  // --- My Report (every internal role) ---
  const downloadMyReport = async (fmt = 'pdf') => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
      const resp = await api.get(`/reports/employee-work/mine?date_from=${start}&date_to=${today}&format=${fmt}`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shine_my_report_${start}_to_${today}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not download your report');
    }
  };

  // --- Possible Duplicates tab (Owner/Admin/Operations only) ---
  const [tab, setTab] = useState('entries'); // 'entries' | 'duplicates'
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const canSeeDuplicates = ['Owner', 'Admin', 'Operations'].includes(user?.role);
  const loadDuplicates = async () => {
    if (!canSeeDuplicates) return;
    setDupLoading(true);
    try {
      const res = await api.get('/daily-reports/possible-duplicates');
      setDupGroups(res.data || []);
    } catch (e) {
      toast.error('Could not load duplicates');
    } finally {
      setDupLoading(false);
    }
  };
  useEffect(() => { if (tab === 'duplicates') loadDuplicates(); }, [tab]);
  const mergeInto = async (primary_id, group) => {
    const merge_ids = group.items.filter((i) => i.id !== primary_id).map((i) => i.id);
    if (!merge_ids.length) return;
    const confirmMsg = `Merge ${merge_ids.length} item${merge_ids.length === 1 ? '' : 's'} into "${(group.items.find((i) => i.id === primary_id)?.item_description || '').slice(0, 60)}"? ` +
      `Hours and collaborators move over. Counts are NOT summed — you can edit the primary's count after.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post('/daily-reports/merge', { primary_id, merge_ids });
      toast.success('Merged');
      loadDuplicates();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    }
  };

  const remove = async (r) => {
    if (!confirm('Delete this entry?')) return;
    await api.delete(`/daily-reports/${r.id}`);
    load();
    loadStats();
  };

  const projectsForClient = useMemo(() => {
    if (!form.client_id) return projects;
    return projects.filter((p) => p.client_id === form.client_id);
  }, [form.client_id, projects]);

  return (
    <div>
      <PageHeader
        icon={ClipboardList}
        title="Daily Reports"
        subtitle="Log every piece of work — company, format, count, hours."
        actions={!isClient && (
          <div className="flex flex-wrap items-center gap-2">
            {isOwner && (
              <Button
                onClick={exportReports}
                variant="outline"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold"
                data-testid="export-reports-btn"
                title="Export all team log reports (Excel)"
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
              </Button>
            )}
            {/* Every internal user can grab their OWN report (this-month, PDF) */}
            <Button
              onClick={() => downloadMyReport('pdf')}
              variant="outline"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 font-semibold"
              data-testid="download-my-report-btn"
              title="Download your own report for this month (PDF)"
            >
              <Download className="h-4 w-4 mr-1" /> My Report
            </Button>
            {['Owner', 'Admin', 'Operations'].includes(user?.role) && (
              <>
                <Button
                  onClick={() => setDownloadOpen(true)}
                  variant="outline"
                  className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 font-semibold"
                  data-testid="download-work-report-btn"
                  title="Download the client-facing work report (counts + status)"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Client Report
                </Button>
                <Button
                  onClick={() => setEmpDownloadOpen(true)}
                  variant="outline"
                  className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 font-semibold"
                  data-testid="download-employee-report-btn"
                  title="Download the internal employee report (hours + names)"
                >
                  <Users className="h-4 w-4 mr-1" /> Employee Report
                </Button>
              </>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-report-btn" onClick={openLogWorkFlow} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> Log Work
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white">{editing ? 'Edit entry' : 'Log work'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date {!canBackdate && <span className="text-[10px] text-amber-300/70">(today only)</span>}</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      min={canBackdate ? undefined : today}
                      max={canBackdate ? undefined : today}
                      disabled={!canBackdate}
                      className="bg-[#0f2240] border-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="report-date"
                    />
                  </div>
                  <div>
                    <Label>Format</Label>
                    <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v, custom_format: v === 'Other' ? form.custom_format : '' })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="report-format"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">{meta.formats.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {form.format === 'Other' && (
                  <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-2.5">
                    <Label className="text-amber-200">Specify the work (Admin will review)</Label>
                    <Input
                      value={form.custom_format}
                      onChange={(e) => setForm({ ...form, custom_format: e.target.value })}
                      placeholder="e.g. Voice-over, Drone shoot, Print Banner…"
                      className="mt-1 bg-[#0f2240] border-amber-500/30"
                      data-testid="report-custom-format"
                    />
                    <div className="text-[11px] text-amber-200/70 mt-1">
                      Your entry will save now. Once Admin approves, it&apos;ll be renamed and added to the format list.
                    </div>
                  </div>
                )}
                <div>
                  <Label>Company</Label>
                  <ClientCombobox
                    clients={clients}
                    value={form.client_id}
                    onChange={(cid, cObj) => setForm({
                      ...form,
                      client_id: cid,
                      company_name: cObj ? (cObj.company || '') : form.company_name,
                      // Reset project when client changes
                      project_id: cid !== form.client_id ? '' : form.project_id,
                    })}
                    placeholder="Search & pick a client…"
                    testIdPrefix="report-client"
                  />
                  {!form.client_id && (
                    <Input
                      placeholder="Or type a custom company name"
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      className="mt-2 bg-[#0f2240] border-amber-500/20"
                      data-testid="report-custom-company"
                    />
                  )}
                </div>
                {projectsForClient.length > 0 && (
                  <div>
                    <Label>Project {form.client_id ? '(optional)' : '(pick a project — client will auto-fill)'}</Label>
                    <Select
                      value={form.project_id || '_none'}
                      onValueChange={(v) => {
                        if (v === '_none') {
                          setForm({ ...form, project_id: '' });
                          return;
                        }
                        const picked = projects.find((p) => p.id === v);
                        // If picking a project from a different client, switch client too
                        if (picked && picked.client_id && picked.client_id !== form.client_id) {
                          setForm({
                            ...form,
                            project_id: v,
                            client_id: picked.client_id,
                            company_name: picked.client_name || form.company_name,
                          });
                        } else {
                          setForm({ ...form, project_id: v });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="report-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-80">
                        <SelectItem value="_none">None</SelectItem>
                        {projectsForClient.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}{!form.client_id && p.client_name ? ` · ${p.client_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>What did you do?</Label><Input value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} placeholder="e.g. Diwali campaign reel — final edit" className="bg-[#0f2240] border-amber-500/20" data-testid="report-desc" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Count</Label><Input type="number" min="1" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="report-count" /></div>
                  <div><Label>Hours</Label><Input type="number" step="0.25" min="0" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="report-hours" /></div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="report-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                {!isClient && (
                  <div>
                    <Label>Collaborators <span className="text-[10px] text-slate-500">(they can log hours here + see it in “My entries”)</span></Label>
                    <CollaboratorPicker
                      users={users.length ? users : [user].filter(Boolean)}
                      value={form.collaborators || []}
                      onChange={(next) => setForm({ ...form, collaborators: next })}
                      exclude={user?.id ? [user.id] : []}
                      testIdPrefix="report-collab"
                    />
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.item_description || !form.date} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="report-save">{editing ? 'Save' : 'Submit'}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />

      {/* Tabs — Entries / Possible Duplicates (admin only) */}
      {!isClient && canSeeDuplicates && (
        <div className="flex items-center gap-1 mb-3 border-b border-amber-500/10 pb-0">
          <button
            type="button"
            onClick={() => setTab('entries')}
            data-testid="tab-entries"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === 'entries' ? 'border-amber-400 text-amber-200' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Entries
          </button>
          <button
            type="button"
            onClick={() => setTab('duplicates')}
            data-testid="tab-duplicates"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-1 ${
              tab === 'duplicates' ? 'border-amber-400 text-amber-200' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <GitMerge className="h-4 w-4" /> Possible Duplicates
          </button>
        </div>
      )}

      {tab === 'duplicates' && canSeeDuplicates ? (
        <DuplicatesPanel
          groups={dupGroups}
          loading={dupLoading}
          users={users}
          onReload={loadDuplicates}
          onMerge={mergeInto}
        />
      ) : (
      <>

      {!isClient && stats && (
        <Card className="shine-card p-5 rounded-xl mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <ScoreRing score={stats.score} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">My Performance</div>
                <h3 className="text-xl font-extrabold text-white">{period === 'week' ? 'Last 7 days' : period === 'month' ? 'Last 30 days' : period === 'today' ? 'Today' : 'All time'}</h3>
                <div className="text-xs text-slate-400 mt-1">Volume + consistency + client rating combined</div>
              </div>
            </div>
            <div className="flex gap-2">
              {['today', 'week', 'month'].map((p) => (
                <Button key={p} variant={period === p ? 'default' : 'outline'} size="sm"
                  onClick={() => setPeriod(p)}
                  className={period === p ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'}>
                  {p === 'today' ? 'Today' : p === 'week' ? 'Week' : 'Month'}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
            <StatCard icon={Hash} label="Items" value={stats.total_items} sub={`${stats.entries} entries`} />
            <StatCard icon={Clock} label="Hours" value={stats.total_hours} />
            <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} sub={`${stats.ongoing} ongoing`} />
            <StatCard icon={Star} label="Avg rating" value={stats.avg_rating ? `${stats.avg_rating}/5` : '—'} sub={`${stats.ratings_count} reviews`} />
            <StatCard icon={Flame} label="Days logged" value={`${stats.days_reported}/${stats.period_days}`} sub={`${stats.consistency_pct}% consistency`} />
            <StatCard icon={TrendingUp} label="Top format" value={(Object.entries(stats.by_format || {}).sort((a, b) => b[1] - a[1])[0]?.[0]) || '—'} />
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap items-center gap-2">
        {!isClient && (
          <>
            <Button size="sm" variant={mine ? 'default' : 'outline'} onClick={() => setMine(true)}
              className={mine ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'} data-testid="filter-mine-reports">My entries</Button>
            {isManager && (
              <Button size="sm" variant={!mine ? 'default' : 'outline'} onClick={() => setMine(false)}
                className={!mine ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'} data-testid="filter-all-reports">All entries</Button>
            )}
          </>
        )}
        <Select value={filters.format || '_all'} onValueChange={(v) => setFilters({ ...filters, format: v === '_all' ? '' : v })}>
          <SelectTrigger className="w-44 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">
            <SelectItem value="_all">All formats</SelectItem>
            {meta.formats.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || '_all'} onValueChange={(v) => setFilters({ ...filters, status: v === '_all' ? '' : v })}>
          <SelectTrigger className="w-36 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="_all">All status</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.client_id || '_all'} onValueChange={(v) => setFilters({ ...filters, client_id: v === '_all' ? '' : v })}>
          <SelectTrigger className="w-44 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Client" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="_all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
          </SelectContent>
        </Select>
        {!mine && isManager && (
          <Select value={filters.user_id || '_all'} onValueChange={(v) => setFilters({ ...filters, user_id: v === '_all' ? '' : v })}>
            <SelectTrigger className="w-48 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Team member" /></SelectTrigger>
            <SelectContent className="bg-[#0a1628] border-amber-500/20">
              <SelectItem value="_all">All members</SelectItem>
              {(reportUsers.length ? reportUsers : users).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {/* Date range — quick chips + custom picker */}
        <div className="flex items-center gap-1 ml-auto">
          {[
            { key: 'today', label: 'Today' },
            { key: 'week', label: 'Last 7d' },
            { key: 'month', label: 'Last 30d' },
            { key: 'all', label: 'All time' },
          ].map((p) => {
            const isActive = (() => {
              if (p.key === 'all') return !filters.date_from && !filters.date_to;
              const today = new Date().toISOString().slice(0, 10);
              const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
              const targets = {
                today: [today, today],
                week: [daysAgo(6), today],
                month: [daysAgo(29), today],
              }[p.key];
              return filters.date_from === targets[0] && filters.date_to === targets[1];
            })();
            const apply = () => {
              if (p.key === 'all') return setFilters({ ...filters, date_from: '', date_to: '' });
              const today = new Date().toISOString().slice(0, 10);
              const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
              const targets = {
                today: [today, today],
                week: [daysAgo(6), today],
                month: [daysAgo(29), today],
              }[p.key];
              setFilters({ ...filters, date_from: targets[0], date_to: targets[1] });
            };
            return (
              <Button
                key={p.key}
                size="sm"
                variant={isActive ? 'default' : 'outline'}
                onClick={apply}
                className={isActive ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628] h-9' : 'border-amber-500/40 text-amber-200 h-9'}
                data-testid={`date-preset-${p.key}`}
              >
                {p.label}
              </Button>
            );
          })}
        </div>
        {/* Custom date pickers — full-width row on wrap */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="h-9 w-40 bg-[#0f2240] border-amber-500/20"
            data-testid="filter-date-from"
          />
          <span className="text-slate-400 text-xs">to</span>
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="h-9 w-40 bg-[#0f2240] border-amber-500/20"
            data-testid="filter-date-to"
          />
          {(filters.date_from || filters.date_to) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFilters({ ...filters, date_from: '', date_to: '' })}
              className="text-slate-400 hover:text-red-300 h-9"
              data-testid="filter-date-clear"
            >
              Clear
            </Button>
          )}
        </div>
      </Card>

      {rows.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          No entries yet. {!isClient && 'Click “Log Work” to add your first one.'}
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.id} className="shine-card p-4 rounded-xl" data-testid={`report-row-${r.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{r.format}</Badge>
                    {r.format === 'Other' && r.custom_format && (
                      <Badge variant="outline" className="border-amber-300/60 text-amber-100 text-[10px]">
                        “{r.custom_format}” · pending Admin review
                      </Badge>
                    )}
                    <Badge variant="outline" className={STATUS_COLOR[r.status]}>{r.status}</Badge>
                    {r.client_review_rating && (
                      <span className="inline-flex items-center text-xs text-amber-200">
                        {'★'.repeat(r.client_review_rating)}{'☆'.repeat(5 - r.client_review_rating)}
                      </span>
                    )}
                  </div>
                  <h4 className="font-bold text-white">{r.item_description}</h4>
                  <div className="mt-1 text-xs text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-amber-300">{r.company_name}</span>
                    {r.project_name && <span>· {r.project_name}</span>}
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(r.date).toLocaleDateString()}</span>
                    <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {r.count}</span>
                    {r.hours_hidden ? (
                      <span className="inline-flex items-center gap-1 text-slate-500" title="Hours restricted by your access">
                        <Lock className="h-3 w-3" /> —
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {r.hours ?? 0}h</span>
                    )}
                    {(isManager || !mine) && r.user_name && <span>· by {r.user_name}</span>}
                  </div>
                  {r.client_review_note && (
                    <div className="mt-2 text-xs text-amber-100/90 bg-amber-500/5 border border-amber-500/15 rounded-md px-2 py-1">
                      “{r.client_review_note}” — {r.client_reviewed_by}
                    </div>
                  )}
                  {r.notes && <div className="mt-1 text-xs text-slate-400">{r.notes}</div>}
                  {Array.isArray(r.collaborators) && r.collaborators.length > 0 && (
                    <div className="mt-2">
                      <CollaboratorChips ids={r.collaborators} users={users} size="xs" />
                    </div>
                  )}
                </div>
                {/* Owner/Admin/Operations OR creator can flip status via a dialog that requires hours */}
                {(() => {
                  const privileged = ['Owner', 'Admin', 'Operations'].includes(user?.role);
                  const isCreator = r.user_id === user?.id;
                  const canFlip = privileged || isCreator;
                  if (!canFlip) return null;
                  return (
                    <div className="flex gap-1 flex-wrap justify-end flex-shrink-0 max-w-full">
                      {r.status === 'Ongoing' && (
                        <Button size="sm" variant="ghost" onClick={() => openStatusDialog(r, 'Submitted')} className="text-sky-300 hover:bg-sky-500/10 h-8 px-2 text-xs" data-testid={`report-mark-submitted-${r.id}`}>Mark Submitted</Button>
                      )}
                      {(r.status === 'Ongoing' || r.status === 'Submitted' || r.status === 'Accepted') && (
                        <Button size="sm" variant="ghost" onClick={() => openStatusDialog(r, 'Completed')} className="text-emerald-300 hover:bg-emerald-500/10 h-8 px-2 text-xs" data-testid={`report-mark-completed-${r.id}`}>Mark Completed</Button>
                      )}
                      {isCreator && r.status === 'Ongoing' && (
                        <Button size="sm" variant="ghost" onClick={() => continueEntry(r)} className="text-amber-300 hover:bg-amber-500/10 h-8 px-2 text-xs" data-testid={`report-log-hours-${r.id}`}>+ Hours</Button>
                      )}
                      {privileged && r.status !== 'Ongoing' && (
                        <Button size="sm" variant="ghost" onClick={() => reopenReport(r)} className="text-slate-300 hover:bg-slate-500/10 h-8 px-2 text-xs" data-testid={`report-reopen-${r.id}`}>Reopen</Button>
                      )}
                      {canEditEntry && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="text-amber-300 hover:bg-amber-500/10 h-8 w-8" data-testid={`report-edit-${r.id}`}><Edit3 className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(r)} className="text-red-400 hover:bg-red-500/10 h-8 w-8" data-testid={`report-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Card>
          ))}
        </div>
      )}
      </>
      )}

      {/* Continue-or-start-new picker */}
      <Dialog open={ongoingOpen} onOpenChange={setOngoingOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-amber-500/10 shrink-0">
            <DialogTitle className="text-white">Continue an Ongoing item?</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 py-4 min-h-0 space-y-2">
            <p className="text-xs text-slate-400">You have Ongoing work already. Add hours to one of these instead of creating a duplicate:</p>
            {ongoingList.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => continueEntry(r)}
                className="w-full text-left bg-[#0f2240] border border-amber-500/20 rounded-md px-3 py-2 hover:border-amber-500/40 focus:outline-none"
                data-testid={`ongoing-pick-${r.id}`}
              >
                <div className="text-sm text-white font-semibold truncate">{r.item_description}</div>
                <div className="text-[11px] text-slate-400">
                  {r.company_name} · {r.format} · {(r.hours || 0)}h total · started {r.date}
                </div>
                {/* Shared-with hint helps the user pick a joint entry instead of duplicating. */}
                {r.user_id && r.user_id !== user?.id && (
                  <div className="text-[10px] text-amber-200 mt-0.5">Owned by {r.user_name || 'a teammate'} · you’re a collaborator</div>
                )}
                {Array.isArray(r.collaborators) && r.collaborators.length > 0 && (
                  <div className="mt-1">
                    <CollaboratorChips ids={r.collaborators} users={users} size="xs" label="Shared with" />
                  </div>
                )}
              </button>
            ))}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-amber-500/10 shrink-0 bg-[#0a1628]">
            <Button variant="outline" onClick={() => { setOngoingOpen(false); openCreate(); }} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid="start-new-anyway">
              Start something new
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log-Hours dialog */}
      <Dialog open={!!logHoursOn} onOpenChange={(o) => !o && setLogHoursOn(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Log hours · {logHoursOn?.item_description}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-slate-400">Company: {logHoursOn?.company_name} · Format: {logHoursOn?.format} · Total so far: {logHoursOn?.hours || 0}h</div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={logHoursForm.date} onChange={(e) => setLogHoursForm({ ...logHoursForm, date: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="log-hours-date" />
            </div>
            <div>
              <Label>Hours (this day)</Label>
              <Input type="number" step="0.25" min="0.25" value={logHoursForm.hours} onChange={(e) => setLogHoursForm({ ...logHoursForm, hours: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="log-hours-hours" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={logHoursForm.note} onChange={(e) => setLogHoursForm({ ...logHoursForm, note: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="e.g. client asked for one more revision" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitLogHours} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="log-hours-save">Log hours</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status change dialog — required-hours gatekeeper for Mark Submitted / Completed */}
      <Dialog open={!!statusTarget} onOpenChange={(v) => !v && setStatusTarget(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Mark {statusTarget?.newStatus || ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-400">
              Enter the hours you spent closing this out. A log line is added for today
              — you can't change status without this (it's how we stop duplicate rows).
            </p>
            <div>
              <Label>Hours worked today</Label>
              <Input type="number" min="0" step="0.1" value={statusForm.hours}
                onChange={(e) => setStatusForm({ ...statusForm, hours: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="status-hours-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={statusForm.note}
                onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 text-white"
                placeholder="e.g. Final version delivered" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitStatusChange} data-testid="status-confirm-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold">
              Confirm {statusTarget?.newStatus || ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Client Report dialog — client REQUIRED, counts + status only */}
      <Dialog open={downloadOpen} onOpenChange={setDownloadOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Client Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] text-slate-400 bg-sky-500/5 border border-sky-500/20 rounded-md px-2 py-1.5">
              Client-safe download — counts & status only. Hours, team names and
              roles are never included.
            </div>
            <div>
              <Label>Client <span className="text-red-400">*</span></Label>
              <select value={downloadForm.client_id}
                onChange={(e) => setDownloadForm({ ...downloadForm, client_id: e.target.value, project_id: '' })}
                className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm"
                data-testid="download-client-select">
                <option value="">— Pick a client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
              </select>
            </div>
            {projectsForDownload.length > 0 && (
              <div>
                <Label>Project (optional)</Label>
                <select value={downloadForm.project_id}
                  onChange={(e) => setDownloadForm({ ...downloadForm, project_id: e.target.value })}
                  className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm"
                  data-testid="download-project-select">
                  <option value="">All projects</option>
                  {projectsForDownload.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => applyDatePreset('this_month')}
                className="border-amber-500/40 text-amber-200 h-8 text-xs" data-testid="download-preset-this-month">
                This month
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyDatePreset('last_month')}
                className="border-amber-500/40 text-amber-200 h-8 text-xs" data-testid="download-preset-last-month">
                Last month
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Input type="date" value={downloadForm.date_from}
                  onChange={(e) => setDownloadForm({ ...downloadForm, date_from: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20 text-white"
                  data-testid="download-date-from" />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={downloadForm.date_to}
                  onChange={(e) => setDownloadForm({ ...downloadForm, date_to: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20 text-white"
                  data-testid="download-date-to" />
              </div>
            </div>
            <div>
              <Label>Format</Label>
              <select value={downloadForm.format}
                onChange={(e) => setDownloadForm({ ...downloadForm, format: e.target.value })}
                className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm">
                <option value="pdf">PDF</option>
                <option value="xlsx">Excel</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDownloadOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button
              onClick={downloadClientReport}
              disabled={!downloadForm.client_id}
              data-testid="download-report-confirm"
              className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Employee Report dialog — internal, hours + names */}
      <Dialog open={empDownloadOpen} onOpenChange={setEmpDownloadOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Employee Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-md px-2 py-1.5 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              INTERNAL ONLY — includes hours and team-member names. Never share with a client.
            </div>
            <div>
              <Label>Employee</Label>
              <select value={empForm.user_id}
                onChange={(e) => setEmpForm({ ...empForm, user_id: e.target.value })}
                className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm"
                data-testid="emp-user-select">
                <option value="all">All employees</option>
                {(reportUsers.length ? reportUsers : users).filter((u) => u.role !== 'Client').map((u) =>
                  <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
                )}
              </select>
            </div>
            <div>
              <Label>Client (optional filter)</Label>
              <select value={empForm.client_id}
                onChange={(e) => setEmpForm({ ...empForm, client_id: e.target.value })}
                className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm"
                data-testid="emp-client-select">
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Input type="date" value={empForm.date_from}
                  onChange={(e) => setEmpForm({ ...empForm, date_from: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20 text-white"
                  data-testid="emp-date-from" />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={empForm.date_to}
                  onChange={(e) => setEmpForm({ ...empForm, date_to: e.target.value })}
                  className="bg-[#0f2240] border-amber-500/20 text-white"
                  data-testid="emp-date-to" />
              </div>
            </div>
            <div>
              <Label>Format</Label>
              <select value={empForm.format}
                onChange={(e) => setEmpForm({ ...empForm, format: e.target.value })}
                className="w-full bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-2 text-white text-sm">
                <option value="pdf">PDF (A4 landscape)</option>
                <option value="xlsx">Excel (one sheet per employee)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmpDownloadOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button
              onClick={downloadEmployeeReport}
              data-testid="emp-report-confirm"
              className="bg-purple-500 hover:bg-purple-400 text-white font-semibold"
            >
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Possible Duplicates panel — Owner/Admin/Operations only. Groups come from
   GET /daily-reports/possible-duplicates. Each group is one (client, project,
   format, normalized description) key with 2+ items. Picking a primary moves
   the others' hour_logs + collaborators into it; the merged rows hide from
   every list and report. */
function DuplicatesPanel({ groups, loading, users, onReload, onMerge }) {
  if (loading) {
    return <Card className="shine-card p-6 text-center text-slate-400">Scanning for duplicates…</Card>;
  }
  if (!groups.length) {
    return (
      <Card className="shine-card p-10 text-center text-slate-400 rounded-xl" data-testid="duplicates-empty">
        <GitMerge className="h-10 w-10 mx-auto mb-3 text-slate-500" />
        No duplicate Work Items detected.
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={onReload}
            className="border-amber-500/40 text-amber-200" data-testid="duplicates-rescan">
            Rescan
          </Button>
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-4" data-testid="duplicates-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-300">
          <span className="font-semibold text-white">{groups.length}</span> group{groups.length === 1 ? '' : 's'} with potential duplicates.
          Pick the item to keep — the others' hours and collaborators move into it.
        </div>
        <Button size="sm" variant="outline" onClick={onReload}
          className="border-amber-500/40 text-amber-200" data-testid="duplicates-rescan">
          Rescan
        </Button>
      </div>
      {groups.map((g) => (
        <Card key={g.key} className="shine-card p-4 rounded-xl" data-testid={`dup-group-${g.key}`}>
          <div className="text-xs uppercase tracking-wider text-amber-200 mb-3">
            {g.items.length} items in this group
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {g.items.map((it) => (
              <div
                key={it.id}
                className="bg-[#0f2240] border border-amber-500/20 rounded-md p-3 text-xs"
                data-testid={`dup-item-${it.id}`}
              >
                <div className="font-semibold text-white truncate" title={it.item_description}>
                  {it.item_description}
                </div>
                <div className="text-slate-400 mt-1">
                  {it.company_name} · {it.project_name || 'no project'} · {it.custom_format || it.format}
                </div>
                <div className="text-slate-500 mt-1">
                  Started {new Date(it.date).toLocaleDateString()} · {it.hours || 0}h · {it.status}
                </div>
                <div className="text-slate-500">By {it.user_name || '—'}</div>
                {Array.isArray(it.collaborators) && it.collaborators.length > 0 && (
                  <div className="mt-1">
                    <CollaboratorChips ids={it.collaborators} users={users} size="xs" label="Shared with" />
                  </div>
                )}
                <Button
                  size="sm"
                  onClick={() => onMerge(it.id, g)}
                  className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold h-8"
                  data-testid={`dup-merge-into-${it.id}`}
                >
                  <GitMerge className="h-3.5 w-3.5 mr-1" /> Merge into this one
                </Button>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
